/**
 * Sending a course to Mauvely Review.
 *
 * ── Why the browser does the whole thing ────────────────────────────────────
 * The `.course` file is an encrypted node like any other, and the key never
 * leaves this browser. So Cloud's Worker cannot open it, cannot validate it and
 * cannot relay it — and Review cannot pull it. The bytes have to travel from
 * here.
 *
 * What does *not* travel from here is authority. The session cookie is
 * `Domain=.mauvely.com`, which means it would be attached to a request to
 * review.mauvely.com — they are different origins but the same site — and it
 * would be attached for every other `*.mauvely.com` origin too, including the
 * one that runs author-written course JavaScript. So the upload authenticates
 * with a short-lived ticket instead: our own Worker asks Review for one over the
 * internal channel, and the `fetch` calls below run with `credentials: 'omit'`.
 * See review/worker/lib/ticket.ts.
 *
 * ── What is reused ──────────────────────────────────────────────────────────
 * The zip reader in `courses.ts` — `readDirectory` and `readEntry` — and
 * `downloadStream` from `nodes.ts`. `publishCourse()` itself is *not* reusable:
 * it requires `index.html` at the root, which is a Compose HTML5 export, and a
 * `.course` is a different artefact entirely.
 */

import { api } from './api';
import { readDirectory, readEntry } from './courses';
import { downloadStream, type DecryptedNode } from './nodes';

export interface SendProgress {
  percent: number;
  phase: 'downloading' | 'checking' | 'uploading' | 'finishing';
}

/** What a `.course` says about itself. Only the fields Review needs. */
export interface CourseSummary {
  title: string;
  lessonCount: number;
  blockCount: number;
  /** Blocks whose media is a plain disk path rather than a staged asset, so the
   *  bytes are not in the archive. Reported to the author before the link goes
   *  out — see the comment on `collectMissingMedia`. */
  missingMedia: string[];
}

/**
 * The marker that says "this really is a Mauvely course".
 *
 * Exactly what `CourseModel::fromJson` checks in Compose
 * (`src/core/document.cpp`). Nothing else writes it, so a renamed `.zip`, a
 * SCORM package or a `.course` from some unrelated tool all fail on the same
 * test the desktop app uses.
 */
const FORMAT = 'course-editor.project';

/**
 * The newest on-disk format this understands.
 *
 * Compose writes `version` and has never read it back, because it only ever
 * opens files it wrote. Review renders someone else's file on a server, so a
 * newer format has to be refused rather than rendered wrong — a silently
 * mangled course is worse than a clear "update Review".
 */
const MAX_VERSION = 1;

/** A zip starts with a local file header. Nothing else does. */
function isZip(buf: ArrayBuffer): boolean {
  if (buf.byteLength < 4) return false;
  const b = new Uint8Array(buf, 0, 4);
  return b[0] === 0x50 && b[1] === 0x4b && b[2] === 0x03 && b[3] === 0x04;
}

/**
 * Media a `.course` names but does not contain.
 *
 * `AssetStore` keeps plain absolute disk paths valid — Compose resolves them
 * from the filesystem when it packs an export. Review has no filesystem, so
 * those blocks would render an empty figure and nobody would know why. Collected
 * here and shown to the author before they send the link.
 */
function collectMissingMedia(course: Record<string, unknown>, staged: Set<string>): string[] {
  const missing: string[] = [];
  const check = (path: unknown, blockId: unknown) => {
    if (typeof path !== 'string' || !path) return;
    // `asset://key` and already-staged relative paths are fine; an absolute
    // path or a Windows drive letter is a file only Compose could see.
    const looksLocal = path.startsWith('/') || /^[A-Za-z]:[\\/]/.test(path);
    if (!looksLocal && (path.startsWith('asset://') || staged.has(path))) return;
    if (looksLocal) missing.push(typeof blockId === 'string' ? blockId : path);
  };

  for (const lesson of (course.lessons as Record<string, unknown>[]) ?? []) {
    for (const block of (lesson.blocks as Record<string, unknown>[]) ?? []) {
      check(block.media, block.id);
      for (const item of (block.freeItems as Record<string, unknown>[]) ?? []) {
        check(item.media, block.id);
      }
    }
  }
  return [...new Set(missing)].slice(0, 500);
}

interface ParsedCourse {
  course: Record<string, unknown>;
  summary: CourseSummary;
  staged: Map<string, Uint8Array>;
}

/**
 * Check the entry document and describe what is in it.
 *
 * Split out from `parseCourse` so the validation can be unit-tested against a
 * string without building a zip.
 */
export function checkCourseJson(
  raw: string,
  staged: Map<string, Uint8Array> = new Map()
): ParsedCourse {
  let course: Record<string, unknown>;
  try {
    course = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    throw new Error("That file isn't a Mauvely course. Save it from Compose and try again.");
  }

  if (course.format !== FORMAT) {
    throw new Error("That file isn't a Mauvely course. Save it from Compose and try again.");
  }
  const version = typeof course.version === 'number' ? course.version : 0;
  if (version > MAX_VERSION) {
    throw new Error(
      'That course was saved by a newer version of Compose than Review understands yet.'
    );
  }

  const lessons = (course.lessons as Record<string, unknown>[]) ?? [];
  if (!Array.isArray(lessons) || lessons.length === 0) {
    throw new Error('That course has no lessons yet, so there is nothing to review.');
  }

  const blockCount = lessons.reduce(
    (n, l) => n + (((l.blocks as unknown[]) ?? []).length || 0),
    0
  );

  return {
    course,
    staged,
    summary: {
      title: typeof course.title === 'string' && course.title ? course.title : 'Untitled course',
      lessonCount: lessons.length,
      blockCount,
      missingMedia: collectMissingMedia(course, new Set(staged.keys()))
    }
  };
}

/** Read a `.course`, container or legacy plain JSON, and check it. */
export async function parseCourse(buf: ArrayBuffer): Promise<ParsedCourse> {
  const staged = new Map<string, Uint8Array>();

  if (!isZip(buf)) {
    // Legacy plain-JSON `.course`. `projectstore.h` says these still load, so
    // they still send.
    return checkCourseJson(new TextDecoder().decode(buf), staged);
  }

  const entries = readDirectory(buf);
  const entry = entries.find((e) => e.path === 'course.json');
  if (!entry) {
    throw new Error(
      'That zip is not a Mauvely course — it has no course.json. Save it from Compose and try again.'
    );
  }

  for (const e of entries) {
    if (e.path === 'course.json') continue;
    const m = /^assets\/(.+)$/.exec(e.path);
    // Anything outside assets/ is not part of a course container. Skipped
    // rather than refused: a stray .DS_Store should not fail a send.
    if (!m) continue;
    staged.set(e.path, await readEntry(buf, e));
  }

  const raw = new TextDecoder().decode(await readEntry(buf, entry));
  return checkCourseJson(raw, staged);
}

/**
 * Send a `.course` node to Review.
 *
 * Returns the project id and the Review URL to open.
 */
export async function sendToReview(
  node: DecryptedNode,
  nodeKey: CryptoKey,
  onProgress: (p: SendProgress) => void,
  existingProjectId?: string
): Promise<{ projectId: string; url: string }> {
  onProgress({ percent: 2, phase: 'downloading' });

  const stream = await downloadStream(node.id, nodeKey, (done, total) => {
    onProgress({
      percent: 2 + Math.round((done / Math.max(1, total)) * 28),
      phase: 'downloading'
    });
  });
  // The whole file in memory: the zip reader seeks backwards to the central
  // directory, which a stream cannot do. A `.course` is tens of megabytes.
  const buf = await new Response(stream).arrayBuffer();

  onProgress({ percent: 32, phase: 'checking' });
  const { course, summary, staged } = await parseCourse(buf);

  // Our own Worker opens the import: it holds the session, checks the plan and
  // presents the internal secret to Review. The ticket it returns is the only
  // credential that crosses an origin.
  const grant = await api.review.handoff({
    sourceNodeId: node.id,
    title: summary.title,
    lessonCount: summary.lessonCount,
    blockCount: summary.blockCount,
    // With a project named, this upload opens that review's next round
    // rather than a new project.
    ...(existingProjectId ? { existingProjectId } : {})
  });

  const put = async (path: string, bytes: Uint8Array) => {
    const res = await fetch(`${grant.reviewOrigin}/api/review/import/${grant.projectId}/files/${path}`, {
      method: 'PUT',
      // No cookie. The ticket is the whole credential — see the header above.
      credentials: 'omit',
      headers: {
        'x-review-ticket': grant.token,
        'content-type': 'application/octet-stream'
      },
      body: bytes as BodyInit
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new Error(body?.error?.message ?? 'Review would not accept that course.');
    }
  };

  onProgress({ percent: 40, phase: 'uploading' });
  await put('course.json', new TextEncoder().encode(JSON.stringify(course)));

  // Sequential, matching `publishCourse`. Each PUT increments a counter the
  // Worker checks its caps against, and concurrent writes to it lose the count.
  let done = 0;
  for (const [path, bytes] of staged) {
    await put(path, bytes);
    done += 1;
    onProgress({
      percent: 42 + Math.round((done / Math.max(1, staged.size)) * 52),
      phase: 'uploading'
    });
  }

  onProgress({ percent: 96, phase: 'finishing' });
  const res = await fetch(`${grant.reviewOrigin}/api/review/import/${grant.projectId}/ready`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'x-review-ticket': grant.token, 'content-type': 'application/json' },
    body: JSON.stringify({ missingMedia: summary.missingMedia })
  });
  if (!res.ok) throw new Error('Review could not finish setting that course up.');

  onProgress({ percent: 100, phase: 'finishing' });
  return {
    projectId: grant.projectId,
    url: `${grant.reviewOrigin}/p/${grant.projectId}`
  };
}
